import React, { useEffect, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { PHOTO_PAGE_SIZE } from './constants';
import { EvidencePhoto } from './model';
import { usePhotoLinks } from './usePhotoLinks';
import PhotoCard from './PhotoCard';

export interface PhotoGridProps {
  warehouseId: string;
  /** Every photo, worst first; the grid shows one page at a time. */
  photos: EvidencePhoto[];
  /** Meridian grid width per card, e.g. 'grid-3' = four per row. */
  cardWidth: string;
}

/** Photos one page at a time; each page's images load in one batch request. */
const PhotoGrid: React.FC<PhotoGridProps> = ({ warehouseId, photos, cardWidth }) => {
  const [page, setPage] = useState(1);
  // Back to page 1 for a new set of photos, during render rather than in an effect, so the old
  // page's links are never requested for the new photos.
  const [pagedPhotos, setPagedPhotos] = useState(photos);
  if (photos !== pagedPhotos) {
    setPagedPhotos(photos);
    setPage(1);
  }
  const pageCount = Math.max(1, Math.ceil(photos.length / PHOTO_PAGE_SIZE));
  const shown = photos.slice((page - 1) * PHOTO_PAGE_SIZE, page * PHOTO_PAGE_SIZE);
  const { links, loading, failed, retry } = usePhotoLinks(warehouseId, shown);
  // One retry per page load when an image fails (usually an expired link), so a broken image
  // can't loop.
  const retried = useRef(false);
  useEffect(() => {
    retried.current = false;
  }, [page, photos]);
  const onImageError = () => {
    if (retried.current) return;
    retried.current = true;
    retry();
  };

  if (photos.length === 0) {
    return <Text color="secondary">No photos in this range.</Text>;
  }
  return (
    <Column spacing="400" data-testid="iq-photo-grid">
      {failed && (
        <Row spacing="300" data-testid="iq-photos-failed">
          <Text type="b300" color="error">
            {"Couldn't load the photos."}
          </Text>
          <Link onClick={retry}>Try again</Link>
        </Row>
      )}
      <Row
        spacing="400"
        wrap="down"
        alignmentVertical="top"
        widths={shown.map(() => cardWidth)}
        data-testid="iq-photos"
      >
        {shown.map(photo => (
          <PhotoCard
            key={photo.photoId}
            photo={photo}
            url={links.get(photo.photoId)?.url}
            loading={loading}
            unavailable={!loading && !failed && !links.get(photo.photoId)?.url}
            onImageError={onImageError}
          />
        ))}
      </Row>
      {pageCount > 1 && (
        <Row alignmentHorizontal="center">
          <Pagination
            currentPage={page}
            numberOfPages={pageCount}
            onChange={setPage}
            data-testid="iq-photo-pagination"
          />
        </Row>
      )}
    </Column>
  );
};

export default PhotoGrid;
